import React, { useState } from 'react';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const PeriodFilter = ({ onFilterChange, className = '' }) => {
  const now = new Date();
  const [mode, setMode] = useState('month'); // 'month', 'custom', 'all'
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth());
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const getMonthRange = (month, year) => {
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0, 23, 59, 59);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
      label: `${MONTHS[month]} ${year}`
    };
  };

  const applyFilter = (newMode, month, year, from, to) => {
    if (newMode === 'all') {
      onFilterChange({ startDate: '', endDate: '', label: 'All Time' });
    } else if (newMode === 'month') {
      const range = getMonthRange(month, year);
      onFilterChange(range);
    } else if (newMode === 'custom') {
      onFilterChange({
        startDate: from || '',
        endDate: to || '',
        label: from && to ? `${from} to ${to}` : from ? `From ${from}` : to ? `Until ${to}` : 'Custom'
      });
    }
  };

  const handleModeChange = (newMode) => {
    setMode(newMode);
    if (newMode === 'month') {
      applyFilter('month', selectedMonth, selectedYear);
    } else if (newMode === 'all') {
      applyFilter('all');
    }
  };

  const navigateMonth = (direction) => {
    let newMonth = selectedMonth + direction;
    let newYear = selectedYear;
    if (newMonth < 0) { newMonth = 11; newYear--; }
    if (newMonth > 11) { newMonth = 0; newYear++; }
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
    applyFilter('month', newMonth, newYear);
  };

  const handleYearChange = (year) => {
    const y = parseInt(year);
    setSelectedYear(y);
    applyFilter('month', selectedMonth, y);
  };

  const handleMonthSelect = (monthIdx) => {
    const m = parseInt(monthIdx);
    setSelectedMonth(m);
    applyFilter('month', m, selectedYear);
  };

  const handleCustomApply = () => {
    applyFilter('custom', null, null, customFrom, customTo);
  };

  const years = [];
  for (let y = now.getFullYear(); y >= 2020; y--) years.push(y);

  return (
    <div className={`bg-white rounded-xl border border-border shadow-sm p-3 ${className}`} data-testid="period-filter">
      <div className="flex flex-wrap items-center gap-2">
        <Calendar className="w-4 h-4 text-muted-foreground" />
        <div className="flex rounded-lg border border-border overflow-hidden">
          <button
            onClick={() => handleModeChange('month')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors ${mode === 'month' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
            data-testid="filter-mode-month"
          >
            Monthly
          </button>
          <button
            onClick={() => handleModeChange('custom')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l ${mode === 'custom' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
            data-testid="filter-mode-custom"
          >
            Custom
          </button>
          <button
            onClick={() => handleModeChange('all')}
            className={`px-3 py-1.5 text-xs font-medium transition-colors border-l ${mode === 'all' ? 'bg-primary text-primary-foreground' : 'bg-background hover:bg-muted'}`}
            data-testid="filter-mode-all"
          >
            All
          </button>
        </div>

        {mode === 'month' && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => navigateMonth(-1)} className="h-8 w-8 p-0" data-testid="prev-month-btn">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Select value={selectedMonth.toString()} onValueChange={handleMonthSelect}>
              <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={i} value={i.toString()}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={selectedYear.toString()} onValueChange={handleYearChange}>
              <SelectTrigger className="w-[80px] h-8 text-xs" data-testid="year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => navigateMonth(1)} className="h-8 w-8 p-0" data-testid="next-month-btn">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}

        {mode === 'custom' && (
          <div className="flex items-center gap-2">
            <Input
              type="date"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              className="h-8 text-xs w-[140px]"
              data-testid="custom-from-date"
            />
            <span className="text-xs text-muted-foreground">to</span>
            <Input
              type="date"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              className="h-8 text-xs w-[140px]"
              data-testid="custom-to-date"
            />
            <Button size="sm" onClick={handleCustomApply} className="h-8 text-xs" data-testid="apply-custom-filter">
              Apply
            </Button>
          </div>
        )}

        {mode === 'all' && (
          <span className="text-xs text-muted-foreground font-medium">Showing all records</span>
        )}
      </div>
    </div>
  );
};

export { PeriodFilter };
